import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import {
  Box,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Card,
  CardContent,
  CardActions,
  Stack,
  Tooltip,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import ReplayIcon from '@mui/icons-material/Replay'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import WarningIcon from '@mui/icons-material/Warning'
import { apiClient } from '../api/client'
import {
  cronJobService,
  cronJobCategories,
  retryAllDeadLetterQueuesSequential,
  DLQ_RETRY_ENDPOINTS,
} from '../api/cronJobService'

const DESTRUCTIVE_JOB_IDS = new Set<string>([
  // Retention jobs delete data — require explicit confirmation.
  'chat-retention',
  'shipment-retention',
  'dispute-retention',
  'finance-retention',
  'notification-retention',
  'location-retention',
  'kyc-retention',
  'subscription-retention',
  'cleanup-expired-notifications',
])

function friendlyDlqLabel(path: string) {
  const m = path.match(/^\/api\/v1\/admin\/([^/]+)\/dlq\/retry$/)
  return m ? m[1] : path
}

interface JobExecution {
  jobId: string
  status: 'idle' | 'running' | 'success' | 'error'
  message?: string
  timestamp?: Date
}

export default function CronJobsPage() {
  const [executions, setExecutions] = useState<Map<string, JobExecution>>(
    new Map()
  )
  const [confirmJob, setConfirmJob] = useState<{
    endpoint: string
    name: string
  } | null>(null)

  const dlqRetryJobId = 'retry-all-dlq'

  const retryAllDlq = useMutation({
    mutationFn: () => retryAllDeadLetterQueuesSequential(),
    onMutate: () => {
      setExecutions((prev) => {
        const newMap = new Map(prev)
        newMap.set(dlqRetryJobId, {
          jobId: dlqRetryJobId,
          status: 'running',
          timestamp: new Date(),
        })
        return newMap
      })
    },
    onSuccess: (data) => {
      setExecutions((prev) => {
        const newMap = new Map(prev)
        newMap.set(dlqRetryJobId, {
          jobId: dlqRetryJobId,
          status: 'success',
          message: data.message,
          timestamp: new Date(),
        })
        return newMap
      })
    },
    onError: (error: any) => {
      setExecutions((prev) => {
        const newMap = new Map(prev)
        newMap.set(dlqRetryJobId, {
          jobId: dlqRetryJobId,
          status: 'error',
          message:
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            'DLQ retry sequence failed',
          timestamp: new Date(),
        })
        return newMap
      })
    },
  })

  const executeJob = useMutation({
    mutationFn: async (endpoint: string) => {
      const service = cronJobService as any
      if (typeof service[endpoint] === 'function') {
        return await service[endpoint]()
      }
      throw new Error(`Job endpoint ${endpoint} not found`)
    },
    onMutate: (endpoint) => {
      const jobId = endpoint
      setExecutions((prev) => {
        const newMap = new Map(prev)
        newMap.set(jobId, {
          jobId,
          status: 'running',
          timestamp: new Date(),
        })
        return newMap
      })
    },
    onSuccess: (data, endpoint) => {
      const jobId = endpoint
      setExecutions((prev) => {
        const newMap = new Map(prev)
        newMap.set(jobId, {
          jobId,
          status: 'success',
          message: data.message || 'Job executed successfully',
          timestamp: new Date(),
        })
        return newMap
      })
    },
    onError: (error: any, endpoint) => {
      const jobId = endpoint
      setExecutions((prev) => {
        const newMap = new Map(prev)
        newMap.set(jobId, {
          jobId,
          status: 'error',
          message:
            error.response?.data?.error ||
            error.message ||
            'Failed to execute job',
          timestamp: new Date(),
        })
        return newMap
      })
    },
  })

  const handleExecute = (endpoint: string, jobId: string, name: string) => {
    if (DESTRUCTIVE_JOB_IDS.has(jobId)) {
      setConfirmJob({ endpoint, name })
      return
    }
    executeJob.mutate(endpoint)
  }

  const retryOneDlq = useMutation({
    mutationFn: (path: string) => apiClient.post(path),
    onMutate: (path) => {
      setExecutions((prev) => {
        const m = new Map(prev)
        m.set(path, { jobId: path, status: 'running', timestamp: new Date() })
        return m
      })
    },
    onSuccess: (_, path) => {
      setExecutions((prev) => {
        const m = new Map(prev)
        m.set(path, {
          jobId: path,
          status: 'success',
          message: 'DLQ retry triggered',
          timestamp: new Date(),
        })
        return m
      })
    },
    onError: (error: any, path) => {
      setExecutions((prev) => {
        const m = new Map(prev)
        m.set(path, {
          jobId: path,
          status: 'error',
          message:
            error?.response?.data?.message ||
            error?.message ||
            'Retry failed',
          timestamp: new Date(),
        })
        return m
      })
    },
  })

  const getJobStatus = (jobId: string): JobExecution | undefined => {
    return executions.get(jobId)
  }

  const dlqStatus = getJobStatus(dlqRetryJobId)
  const dlqRunning =
    dlqStatus?.status === 'running' || retryAllDlq.isPending

  return (
    <Box>
      {cronJobCategories.map((category) => (
        <Accordion key={category.id} defaultExpanded={category.id === 'shipment-jobs'}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                {category.name}
              </Typography>
              <Chip
                label={`${category.jobs.length} jobs`}
                size="small"
                sx={{ mr: 2 }}
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {category.description}
            </Typography>
            <Grid container spacing={2}>
              {category.jobs.map((job) => {
                const jobStatus = getJobStatus(job.endpoint)
                const isRunning =
                  jobStatus?.status === 'running' ||
                  (executeJob.isPending &&
                    executeJob.variables === job.endpoint)

                return (
                  <Grid item xs={12} sm={6} md={4} key={job.id}>
                    <Card
                      variant="outlined"
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" gutterBottom>
                          {job.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 2 }}
                        >
                          {job.description}
                        </Typography>
                        {jobStatus && (
                          <Box sx={{ mt: 2 }}>
                            {jobStatus.status === 'success' && (
                              <Alert
                                severity="success"
                                icon={<CheckCircleIcon />}
                                sx={{ mb: 1 }}
                              >
                                {jobStatus.message}
                                {jobStatus.timestamp && (
                                  <Typography variant="caption" display="block">
                                    {jobStatus.timestamp.toLocaleTimeString()}
                                  </Typography>
                                )}
                              </Alert>
                            )}
                            {jobStatus.status === 'error' && (
                              <Alert
                                severity="error"
                                icon={<ErrorIcon />}
                                sx={{ mb: 1 }}
                              >
                                {jobStatus.message}
                                {jobStatus.timestamp && (
                                  <Typography variant="caption" display="block">
                                    {jobStatus.timestamp.toLocaleTimeString()}
                                  </Typography>
                                )}
                              </Alert>
                            )}
                            {jobStatus.status === 'running' && (
                              <Alert severity="info" sx={{ mb: 1 }}>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                  }}
                                >
                                  <CircularProgress size={16} />
                                  Executing...
                                </Box>
                              </Alert>
                            )}
                          </Box>
                        )}
                      </CardContent>
                      <CardActions>
                        <Button
                          variant="contained"
                          color={
                            DESTRUCTIVE_JOB_IDS.has(job.id) ? 'warning' : 'primary'
                          }
                          startIcon={
                            isRunning ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : DESTRUCTIVE_JOB_IDS.has(job.id) ? (
                              <WarningIcon />
                            ) : (
                              <PlayArrowIcon />
                            )
                          }
                          onClick={() => handleExecute(job.endpoint, job.id, job.name)}
                          disabled={isRunning}
                          fullWidth
                        >
                          {isRunning
                            ? 'Executing...'
                            : DESTRUCTIVE_JOB_IDS.has(job.id)
                            ? 'Execute (destructive)…'
                            : 'Execute Now'}
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                )
              })}
            </Grid>
          </AccordionDetails>
        </Accordion>
      ))}

      <Accordion defaultExpanded={false}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Retry Dead Letters
            </Typography>
            <Chip
              label={`${DLQ_RETRY_ENDPOINTS.length + 1} actions`}
              size="small"
              sx={{ mr: 2 }}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Replay dead-letter messages. Each service's DLQ has its own
            endpoint; the bulk action runs them sequentially and stops on the
            first error.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" gutterBottom>
                    All DLQ queues
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Sequential POST to every service DLQ retry endpoint.
                  </Typography>
                  {dlqStatus?.status === 'success' && (
                    <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 1 }}>
                      {dlqStatus.message}
                      {dlqStatus.timestamp && (
                        <Typography variant="caption" display="block">
                          {dlqStatus.timestamp.toLocaleTimeString()}
                        </Typography>
                      )}
                    </Alert>
                  )}
                  {dlqStatus?.status === 'error' && (
                    <Alert severity="error" icon={<ErrorIcon />} sx={{ mb: 1 }}>
                      {dlqStatus.message}
                      {dlqStatus.timestamp && (
                        <Typography variant="caption" display="block">
                          {dlqStatus.timestamp.toLocaleTimeString()}
                        </Typography>
                      )}
                    </Alert>
                  )}
                  {dlqRunning && (
                    <Alert severity="info" sx={{ mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} />
                        Running DLQ retries sequentially…
                      </Box>
                    </Alert>
                  )}
                </CardContent>
                <CardActions>
                  <Button
                    variant="contained"
                    startIcon={
                      retryAllDlq.isPending ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <ReplayIcon />
                      )
                    }
                    onClick={() => retryAllDlq.mutate()}
                    disabled={retryAllDlq.isPending}
                    fullWidth
                  >
                    {retryAllDlq.isPending
                      ? 'Running…'
                      : 'Run all DLQ retries (sequential)'}
                  </Button>
                </CardActions>
              </Card>
            </Grid>

            {DLQ_RETRY_ENDPOINTS.map((path) => {
              const status = executions.get(path)
              const isRunning = status?.status === 'running'
              return (
                <Grid item xs={12} sm={6} md={4} key={path}>
                  <Card
                    variant="outlined"
                    sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ mb: 1 }}
                      >
                        <Typography variant="subtitle1" fontWeight={600}>
                          {friendlyDlqLabel(path)}
                        </Typography>
                        <Chip label="DLQ" size="small" variant="outlined" />
                      </Stack>
                      <Tooltip title={path}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: 'block',
                            fontFamily: 'monospace',
                            wordBreak: 'break-all',
                          }}
                        >
                          {path}
                        </Typography>
                      </Tooltip>
                      {status?.status === 'success' && (
                        <Alert severity="success" sx={{ mt: 1 }} icon={<CheckCircleIcon />}>
                          {status.message}
                        </Alert>
                      )}
                      {status?.status === 'error' && (
                        <Alert severity="error" sx={{ mt: 1 }} icon={<ErrorIcon />}>
                          {status.message}
                        </Alert>
                      )}
                    </CardContent>
                    <CardActions>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={
                          isRunning ? <CircularProgress size={14} /> : <ReplayIcon />
                        }
                        disabled={isRunning}
                        onClick={() => retryOneDlq.mutate(path)}
                        fullWidth
                      >
                        {isRunning ? 'Retrying…' : 'Retry'}
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              )
            })}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Confirmation dialog for destructive jobs */}
      <Dialog
        open={!!confirmJob}
        onClose={() => setConfirmJob(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm destructive action</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Alert severity="warning" icon={<WarningIcon />}>
              <strong>{confirmJob?.name}</strong>
            </Alert>
            <Typography variant="body2">
              This job deletes or permanently changes data and cannot be undone.
              Make sure you understand the impact before continuing.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmJob(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={<PlayArrowIcon />}
            onClick={() => {
              if (confirmJob) {
                executeJob.mutate(confirmJob.endpoint)
                setConfirmJob(null)
              }
            }}
          >
            Run Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

